import { useCallback, useMemo, useState } from 'react';
import { useDebouncedValue } from '@/hooks/useDebounceValue';
import { Selector } from '..';
import { useFetchInfinityOptions } from '../hooks/useFetchInfinityOptions';
import { useGetBaseComponents } from '../hooks/useGetBaseComponents';
import type { SelectorOption, SelectorProps } from '../types';
import { SelectorOptionTypes } from '../types';

interface InfinityAsyncSelectorProps<T> extends Omit<SelectorProps<string | number>, 'value' | 'options'> {
  onChange: (option: SelectorOption<string | number> | null) => void;
  label: string;
  placeholder: string;
  value: string | number;
  url: string;
  mapOptions: (options: T[]) => SelectorOption<string | number>[];
  queryKey: string[];
  option?: SelectorOption<string | number> | null;
  isClearable?: boolean;
  isClearOption?: boolean;
  clearOptionLabel?: string;
  helperText?: string;
  staleTime?: number;
  gcTime?: number;
}

export const InfinityAsyncSelector = <T,>({
  onChange,
  url,
  queryKey,
  label,
  placeholder,
  value,
  option,
  mapOptions,
  helperText,
  isClearable = false,
  isClearOption = true,
  clearOptionLabel,
  optionType = SelectorOptionTypes.DEFAULT,
  staleTime,
  gcTime,
  ...rest
}: InfinityAsyncSelectorProps<T>) => {
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebouncedValue(searchValue);
  const components = useGetBaseComponents(optionType);

  const {
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    data: rawOptions,
  } = useFetchInfinityOptions({
    queryKey,
    searchValue: debouncedSearchValue,
    url,
    staleTime,
    gcTime,
  });

  const options = useMemo(() => mapOptions((rawOptions as T[]) ?? []), [rawOptions, mapOptions]);

  const onMenuScrollToBottom = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const onInputChange = (val: string) => {
    setSearchValue?.(val);
  };

  return (
    <Selector
      {...rest}
      value={value}
      option={option}
      menuPosition="fixed"
      label={label}
      options={options}
      optionType={optionType}
      onChange={onChange}
      isLoading={isLoading || isFetchingNextPage}
      placeholder={placeholder}
      onInputChange={onInputChange}
      onMenuScrollToBottom={onMenuScrollToBottom}
      components={components}
      isClearable={isClearable}
      isClearOption={isClearOption}
      clearOptionLabel={clearOptionLabel}
      helperText={helperText}
    />
  );
};
